import { BalancePercentageOptionsEnum } from "components/TradeLeg/sections/BalancePercentages";
import { useGetMaxAmountToWrap } from "hooks/wrap/useGetMaxAmountToWrap";
import { GlobalStore, MakerToken, SelectedToken } from "store/global/GlobalStore";
import { Balance } from "store/global/getBalances";
import { Amount } from "types/amount";
import { Quote } from "types/quote";
import { Chain, Hash } from "viem";
import { isNativeToken } from "./wrapUtils";
import { cloneDeep } from "lodash-es";
import { isAddressEqual } from "./tokenUtils";

export type CounterParty = "maker" | "taker";

export const canLock = (counterParty: CounterParty, counterPartyTokens: Array<SelectedToken>) => {
  if (counterParty === "maker") {
    // We only allow locking if there are more than two unlocked entries remaining
    return (counterPartyTokens as Array<MakerToken>).filter(entry => !entry.isLocked).length > 2;
  }
  return false;
};

export const getAmount = (
  tokenAddress: Hash,
  quote: Quote | undefined,
  counterParty: CounterParty,
  selectedToken: SelectedToken | undefined
): Amount => {
  if (counterParty === "taker" && selectedToken?.amount) {
    return selectedToken.amount;
  }
  if (tokenAddress && quote) {
    const makerToken = quote.makerTokens[tokenAddress];
    if (makerToken) {
      return makerToken.amount ?? Amount.ZERO;
    }
  }
  return Amount.ZERO;
};

export const handleBalanceOption = async (
  tokenAddress: Hash,
  counterParty: CounterParty,
  balanceOption: BalancePercentageOptionsEnum,
  network: Chain,
  balance: Balance,
  getMaxAmountToWrap: ReturnType<typeof useGetMaxAmountToWrap>,
  decimals: number
) => {
  // Wipe the quote since updating the tokens takes time and the quote is no longer valid
  GlobalStore.update(s => {
    s.quote = undefined;
  });
  const percent =
    balanceOption !== BalancePercentageOptionsEnum.Max ? new Amount(balanceOption) : Amount.ZERO;
  if (counterParty === "taker" && tokenAddress) {
    let amountToUpdate: Amount = Amount.ZERO;
    if (isNativeToken(tokenAddress, network)) {
      const maxAmount = (await getMaxAmountToWrap(tokenAddress)) ?? Amount.ZERO;
      amountToUpdate =
        balanceOption === BalancePercentageOptionsEnum.Max
          ? maxAmount
          : new Amount(maxAmount.mul(percent).value.toFixed(decimals));
    } else {
      const currentBalance: Amount = balance?.balance ?? Amount.ZERO;
      amountToUpdate =
        balanceOption === BalancePercentageOptionsEnum.Max
          ? currentBalance
          : new Amount(currentBalance.mul(percent).value.toFixed(decimals));
    }

    GlobalStore.update(s => {
      s.takerTokens = s.takerTokens.map(entry => {
        if (isAddressEqual(entry.tokenAddress, tokenAddress)) {
          const updatedEntry = cloneDeep(entry);
          updatedEntry.amount = amountToUpdate;
          return updatedEntry;
        }
        return entry;
      });
    });
  }
};
